import { BUILD_INFO } from '../../generated/buildInfo';
import { eventBus, EVENTS } from '../EventBus';
import type { AppUpdateState, ScreenPayload } from '../types';
import { isApplicationCache, isObsoleteApplicationCache } from './CachePolicySystem';

export class AppUpdateManager {
  private registration?: ServiceWorkerRegistration;
  private state: AppUpdateState = {
    buildTimestamp: BUILD_INFO.buildTimestamp,
    serviceWorkerVersion: BUILD_INFO.serviceWorkerVersion,
    cacheVersion: BUILD_INFO.cacheVersion,
    updateAvailable: false,
    online: typeof navigator === 'undefined' ? true : navigator.onLine,
  };
  private applying = false;
  private idle = true;

  async start(): Promise<void> {
    if (!('serviceWorker' in navigator) || !import.meta.env.PROD) { this.emit(); return; }
    this.registration = await navigator.serviceWorker.register('./sw.js', { updateViaCache: 'none' });
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (this.applying) window.location.reload();
    });
    window.addEventListener('online', () => { this.state.online = true; this.emit(); void this.check(); });
    window.addEventListener('offline', () => { this.state.online = false; this.emit(); });
    this.watchRegistration(this.registration);
    await this.check();
    // Startup is always an idle boundary. The first attract event can fire before
    // service-worker registration completes, so apply a previously waiting build here.
    if (this.registration.waiting) this.applyWaitingUpdate();
  }

  async check(): Promise<AppUpdateState> {
    this.state.lastUpdateCheck = new Date().toISOString();
    this.state.online = navigator.onLine;
    try {
      await this.registration?.update();
      if (this.registration?.waiting) this.state.updateAvailable = true;
    } catch {
      // Offline is expected at kiosk events; the current cached build remains usable.
    }
    this.emit();
    return this.getState();
  }

  async onScreen(screen: ScreenPayload['name']): Promise<void> {
    this.idle = screen === 'attract';
    if (!this.idle) return;
    await this.check();
    if (this.registration?.waiting) this.applyWaitingUpdate();
  }

  async clearAppCacheAndReload(): Promise<void> {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.map((registration) => registration.unregister()));
    const keys = await caches.keys();
    await Promise.all(keys.filter(isApplicationCache).map((key) => caches.delete(key)));
    const url = new URL(window.location.href);
    url.searchParams.set('fresh', Date.now().toString());
    window.location.replace(url.toString());
  }

  async clearObsoleteCaches(): Promise<void> {
    const keys = await caches.keys();
    await Promise.all(keys.filter((key) => isObsoleteApplicationCache(key, BUILD_INFO.cacheVersion)).map((key) => caches.delete(key)));
  }

  getState(): AppUpdateState { return { ...this.state }; }

  private watchRegistration(registration: ServiceWorkerRegistration): void {
    if (registration.waiting) this.state.updateAvailable = true;
    registration.addEventListener('updatefound', () => {
      const worker = registration.installing;
      worker?.addEventListener('statechange', () => {
        if (worker.state !== 'installed') return;
        if (navigator.serviceWorker.controller) {
          this.state.updateAvailable = true;
          this.emit();
          if (this.idle) window.setTimeout(() => this.applyWaitingUpdate(), 0);
        } else {
          worker.postMessage({ type: 'SKIP_WAITING' });
        }
      });
    });
  }

  private applyWaitingUpdate(): void {
    if (!this.registration?.waiting || this.applying) return;
    this.applying = true;
    this.registration.waiting.postMessage({ type: 'SKIP_WAITING' });
  }

  private emit(): void { eventBus.emit<AppUpdateState>(EVENTS.update, this.getState()); }
}

export const appUpdateManager = new AppUpdateManager();
